import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Layers, CheckSquare, X } from "lucide-react";
import { toast } from "sonner";

export function useBulkSelect() {
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const exit = () => { setSelectMode(false); setSelected(new Set()); };
  return { selectMode, setSelectMode, selected, toggleSel, exit };
}

export function BulkSelectToggle({ selectMode, onEnter, onExit }) {
  return selectMode ? (
    <button onClick={onExit} data-testid="bulk-cancel-button" className="flex items-center gap-1.5 text-sm text-slate-400">
      <X className="h-4 w-4" /> Cancel
    </button>
  ) : (
    <button onClick={onEnter} data-testid="bulk-select-button" className="flex items-center gap-1.5 text-sm font-semibold text-primary">
      <CheckSquare className="h-4 w-4" /> Select
    </button>
  );
}

export function BulkStatusBar({ count, statuses, onDone }) {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!status || count === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk/status", { lead_ids: onDone.ids(), status_slug: status });
      if (data.failed_count) toast.warning(`${data.updated} updated · ${data.failed_count} failed`);
      else toast.success(`${data.updated} leads updated`);
      setStatus("");
      onDone.done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div data-testid="bulk-action-bar" className="fixed bottom-[70px] left-0 right-0 z-40 px-3">
      <div className="glass rounded-2xl border border-slate-700 p-3 flex items-center gap-2 shadow-xl max-w-lg mx-auto">
        <div className="flex items-center gap-1.5 text-sm font-semibold shrink-0"><Layers className="h-4 w-4 text-primary" />{count}</div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="bulk-status-select" className="bg-slate-900/60 border-slate-700 flex-1"><SelectValue placeholder="Change status to…" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            {statuses.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={apply} disabled={busy || !status || count === 0} data-testid="bulk-apply-button" className="bg-primary text-primary-foreground shrink-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
        </Button>
      </div>
    </div>
  );
}
